import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { baseApi } from './baseApi';

export interface SnippetListResponse {
  status: number;
  result: SnippetListData[];
}

export interface SnippetListData {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
}

export interface SnippetDetailsResponse {
  status: number;
  result: SnippetDetailsData;
}

export interface SnippetDetailsData {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  notes: any;
  user: SnippetUser;
  updated_by: SnippetUpdatedBy;
  content: string;
}

export interface SnippetUser {
  id: string;
  name: string;
  email: string;
  avatar: any;
}

export interface SnippetUpdatedBy {
  id: string;
  name: string;
  avatar: any;
}

export const snippetsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    // Queries
    getSnippetList: build.query<
      SnippetListData[],
      { workspace_id: string | number }
    >({
      queryFn: async ({ workspace_id }, _, __, baseQuery) => {
        const url = `/snippets?workspace_id=${workspace_id}`;
        const result = await baseQuery(url);

        if (result.error) {
          return { error: result.error as FetchBaseQueryError };
        }

        const response = result.data as SnippetListResponse;
        return { data: response.result };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Snippet' as const, id })),
              { type: 'Snippet' as const, id: 'LIST' },
            ]
          : [{ type: 'Snippet' as const, id: 'LIST' }],
    }),
    getSnippetDetails: build.query<
      SnippetDetailsData,
      { id: string; taskId?: string; workspace_id: string | number }
    >({
      query: ({ id, taskId, workspace_id }) =>
        `/snippets/${id}/detail?parse_content=true&task_id=${taskId}&workspace_id=${workspace_id}`,
      transformResponse: (response: SnippetDetailsResponse) => response.result,
      providesTags: [{ type: 'Snippet', id: 'LIST' }],
    }),
    getSnippetCollabList: build.query<
      SnippetListData[],
      { websiteId: string; taskId?: string; workspace_id: string | number }
    >({
      queryFn: async (
        { websiteId, taskId, workspace_id },
        _,
        __,
        baseQuery
      ) => {
        const url = `/collaborate/site/${websiteId}/snippets?workspace_id=${workspace_id}&all_Details=true`;
        const result = await baseQuery(url);

        if (result.error) {
          return { error: result.error as FetchBaseQueryError };
        }

        const response = result.data as SnippetListResponse;
        return { data: response.result };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Snippet' as const, id })),
              { type: 'Snippet' as const, id: 'LIST' },
            ]
          : [{ type: 'Snippet' as const, id: 'LIST' }],
    }),
    getCollabSnippetDetails: build.query<
      SnippetDetailsData,
      {
        id: string;
        taskId?: string;
        websiteId: string;
        workspace_id: string | number;
      }
    >({
      queryFn: async (
        { id, taskId, websiteId, workspace_id },
        _,
        __,
        baseQuery
      ) => {
        const url = `/collaborate/site/${websiteId}/snippets/${id}?workspace_id=${workspace_id}&all_Details=true&parse_content=true${taskId ? `&task_id=${taskId}` : ''}`;
        const result = await baseQuery(url);

        if (result.error) {
          return { error: result.error as FetchBaseQueryError };
        }

        const response = result.data as SnippetDetailsResponse;
        return { data: response.result };
      },
      providesTags: [{ type: 'Snippet', id: 'LIST' }],
    }),
    // Mutations
  }),
});

export const {
  //queries
  useGetSnippetListQuery,
  useGetSnippetDetailsQuery,
  useGetSnippetCollabListQuery,
  useGetCollabSnippetDetailsQuery,
  //mutations
} = snippetsApi;
